import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, CalendarDays, Loader2, Search } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import { api } from "@/lib/api";

const endTime = (start, dur) => {
    const [h, m] = start.split(":").map(Number);
    const e = h * 60 + m + dur;
    return `${String(Math.floor(e / 60)).padStart(2, "0")}:${String(e % 60).padStart(2, "0")}`;
};

export default function SubscriberPlanning() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null); // null | planning data
    const [error, setError] = useState("");

    const search = async () => {
        if (!/\S+@\S+\.\S+/.test(email)) {
            setError("Saisissez l'email utilisé lors de votre abonnement");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const { data } = await api.get(`/subscribers/planning?email=${encodeURIComponent(email)}`);
            setResult(data);
        } catch {
            setError("Recherche impossible, réessayez");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="espace-abonne" className="relative py-24 md:py-32 px-6 md:px-12" data-testid="subscriber-planning-section">
            <div className="max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="rounded-3xl border border-blue-400/20 bg-gradient-to-br from-blue-500/[0.07] via-white/[0.02] to-transparent p-8 md:p-12"
                >
                    <span className="ornament font-body text-[10px] uppercase tracking-[0.4em] text-blue-300/80">
                        Espace abonné
                    </span>
                    <h2 className="mt-4 font-display text-3xl md:text-4xl font-extralight tracking-tight text-white leading-[1.1]">
                        Votre planning,{" "}
                        <span className="font-serif-luxe italic text-gold-gradient">en un email.</span>
                    </h2>
                    <p className="mt-4 text-sm md:text-base text-zinc-400 font-body max-w-xl">
                        Abonné·e Mon Séraphin ? Entrez l'email de votre abonnement pour voir vos sessions
                        à venir et vos heures restantes — ou choisir directement votre prochain créneau.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); setResult(null); setError(""); }}
                            onKeyDown={(e) => e.key === "Enter" && search()}
                            placeholder="votre@email.fr"
                            className="flex-1 rounded-xl border border-white/10 bg-black/40 px-5 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:border-blue-400/60 focus:outline-none font-body"
                            data-testid="subscriber-search-input"
                        />
                        <button
                            onClick={search}
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-500 px-6 py-3.5 text-sm font-medium text-white hover:bg-blue-400 transition-colors disabled:opacity-50"
                            data-testid="subscriber-search-btn"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            Voir mon planning
                        </button>
                    </div>
                    {error && <p className="mt-3 text-sm text-red-300 font-body" data-testid="subscriber-search-error">{error}</p>}

                    {result && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="mt-8 rounded-2xl border border-white/10 bg-black/40 p-6"
                            data-testid="subscriber-planning-result"
                        >
                            {result.active ? (
                                <>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-400/40 bg-blue-400/10 px-3 py-1 text-xs text-blue-300">
                                            <BadgeCheck className="w-3.5 h-3.5" />
                                            Abonnement actif jusqu'au {new Date(result.active_until).toLocaleDateString("fr-FR")}
                                        </span>
                                        <span className="text-xs text-zinc-400 font-body" data-testid="subscriber-remaining">
                                            {result.week_remaining_min / 60}h restantes cette semaine
                                        </span>
                                    </div>
                                    {result.name && <p className="mt-3 font-display text-lg text-white">Bonjour {result.name}</p>}

                                    <div className="mt-5">
                                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-body">Vos prochaines sessions</p>
                                        {result.upcoming.length === 0 ? (
                                            <p className="mt-2 text-sm text-zinc-400 font-body" data-testid="subscriber-no-session">
                                                Aucune session réservée — choisissez votre créneau ci-dessous.
                                            </p>
                                        ) : (
                                            <ul className="mt-3 space-y-2" data-testid="subscriber-sessions-list">
                                                {result.upcoming.map((b) => (
                                                    <li
                                                        key={b.id}
                                                        className="flex items-center gap-3 rounded-lg border border-blue-400/25 bg-blue-400/[0.06] px-4 py-2.5"
                                                        data-testid={`subscriber-session-${b.id}`}
                                                    >
                                                        <CalendarDays className="w-4 h-4 text-blue-300 shrink-0" />
                                                        <span className="text-sm text-white font-body">
                                                            {format(new Date(`${b.date}T12:00:00`), "EEEE d MMMM", { locale: fr })}
                                                        </span>
                                                        <span className="text-sm text-blue-200 font-body">
                                                            {b.time_slot} → {endTime(b.time_slot, b.duration_minutes || 60)} · {(b.duration_minutes || 60) / 60}h
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <Link
                                        to={`/reserver?package=abonnement&mode=session&email=${encodeURIComponent(email)}`}
                                        className="mt-6 inline-flex items-center justify-center rounded-xl bg-blue-500 px-6 py-3 text-sm font-medium text-white hover:bg-blue-400 transition-colors"
                                        data-testid="subscriber-book-cta"
                                    >
                                        Choisir un créneau
                                    </Link>
                                </>
                            ) : (
                                <div data-testid="subscriber-not-active">
                                    <p className="text-sm text-zinc-300 font-body">
                                        Aucun abonnement actif pour cet email.
                                    </p>
                                    <p className="mt-1 text-xs text-zinc-500 font-body">
                                        Vérifiez l'adresse saisie, ou découvrez l'abonnement : 3h de studio par semaine, créneaux garantis.
                                    </p>
                                    <Link
                                        to="/reserver?package=abonnement"
                                        className="mt-5 inline-flex items-center justify-center rounded-xl bg-amber-300 px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-amber-200 transition-colors"
                                        data-testid="subscriber-subscribe-cta"
                                    >
                                        Découvrir l'abonnement — 350€/mois
                                    </Link>
                                </div>
                            )}
                        </motion.div>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
